/**
 * DDOptionsGrid - Grid container for displaying multiple design direction cards
 *
 * Responsive layout: 2 columns on desktop, 1 column on mobile
 * Handles empty state and loading skeleton
 * Fetches design directions from backend when campaignId is provided
 */

import { FC, memo, useCallback, useState, useEffect, useRef } from 'react';
import { Box, SimpleGrid, Text, Flex, Button, VStack, Spinner } from '@chakra-ui/react';
import DDPreviewCard, { DesignDirection } from './DDPreviewCard';
import useCopilotChatContext from '../hooks/useCopilotChatContext';
import { getCampaign } from 'src/services/campaign';
import LoadingList from 'src/components/campaigns/design-directions/LoadingList';
import FusionLoading from 'src/components/common/FusionLoading';

interface DDOptionsGridProps {
  campaignId: string;
  selectedDesignDirectionId?: string | null;
  onSelect?: (id: string) => void;
  onRegenerate?: (id: string, feedback?: string) => void;
}

// Module-level cache: once a DD is selected for a campaign, freeze the grid data
// This survives component remounts (caused by conversation re-renders) and prevents
// layout shifts when the backend deletes non-selected DDs
const selectedCampaignCache = new Map<string, DesignDirection[]>();

// Loading card — uses the same animated 3-step LoadingList that the campaign UI shows
// while DDs are generating. Keeps the visual continuity between the chat preview and the
// full campaign view the user lands on after selection.
const LoadingCard: FC<{ variant: string }> = ({ variant }) => (
  <Box
    borderRadius="lg"
    overflow="hidden"
    bg="white"
    border="1px solid"
    borderColor="gray.200"
  >
    <Box pb="100%" position="relative">
      <Box position="absolute" top={0} left={0} right={0} bottom={0}>
        <LoadingList
          isPendingGeneration
          text1="Building layout"
          text2="Generating ad"
          text3="Splitting in multiple layers"
          height="100%"
          width="100%"
          size="sm"
          borderRadius="0"
        />
      </Box>
    </Box>
    <Flex p={3} align="center" justify="space-between" borderTop="1px solid" borderColor="gray.100">
      <Box>
        <Text fontSize="sm" fontWeight="bold" color="gray.700">Design {variant}</Text>
        <Text fontSize="xs" color="gray.400">Generating...</Text>
      </Box>
    </Flex>
  </Box>
);

const DDOptionsGrid: FC<DDOptionsGridProps> = memo(
  ({
    campaignId,
    selectedDesignDirectionId,
    onSelect,
    onRegenerate,
  }) => {
    const { sendMessage } = useCopilotChatContext();
    // If this campaign already had a DD selected (even across remounts), use cached data
    const cachedDDs = selectedCampaignCache.get(campaignId);
    const [designDirections, setDesignDirections] = useState<DesignDirection[]>(cachedDDs || []);
    const [isLoading, setIsLoading] = useState(!cachedDDs);
    const [error, setError] = useState<string | null>(null);
    const [hasSelected, setHasSelected] = useState(!!cachedDDs); // Prevent multiple selections
    const hasSelectedRef = useRef(!!cachedDDs); // Ref mirror for use in polling callback
    const pollingRef = useRef<NodeJS.Timeout | null>(null);
    const mountedRef = useRef(true);

    // Fetch design directions from campaign
    const fetchDesignDirections = useCallback(async () => {
      if (!campaignId) {
        setIsLoading(false);
        return;
      }

      try {
        const campaign = await getCampaign(campaignId);

        const dds: DesignDirection[] = (campaign.designDirections || []).map((dd: any) => ({
          id: dd.id || dd._id,
          variant: dd.variant || 'A',
          status: dd.status || 'GENERATED',
          locked: dd.locked,
          adEngine: dd.adEngine,
          estimatedLeads: dd.estimatedLeads,
          attributes: dd.attributes,
        }));

        if (mountedRef.current) {
          // Check if any DDs are still generating
          const hasGenerating = dds.some(dd => dd.status !== 'GENERATED');

          // After user selects a DD, don't update the grid at all (avoid flicker as backend deletes DDs)
          // Just keep polling until cleanup is done (1 or fewer DDs remain), then stop
          if (hasSelectedRef.current) {
            if (dds.length <= 1) {
              if (pollingRef.current) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
              }
            }
            setIsLoading(false);
            return;
          }

          setDesignDirections(dds);
          setError(null);

          // If all DDs are generated, stop polling
          if (!hasGenerating && dds.length > 0) {
            if (pollingRef.current) {
              clearInterval(pollingRef.current);
              pollingRef.current = null;
            }
          }

          setIsLoading(false);
        }
      } catch (err) {
        console.error('[DDOptionsGrid] Error fetching design directions:', err);
        if (mountedRef.current) {
          setError('Failed to load design options');
          setIsLoading(false);
        }
      }
    }, [campaignId]);

    // Initial fetch and polling (skip if selection was already made for this campaign)
    useEffect(() => {
      if (selectedCampaignCache.has(campaignId)) {
        // Already selected - no need to fetch or poll
        return;
      }

      mountedRef.current = true;

      // Initial fetch
      fetchDesignDirections();

      // Start polling every 3 seconds to check for DD generation completion
      pollingRef.current = setInterval(() => {
        fetchDesignDirections();
      }, 3000);

      return () => {
        mountedRef.current = false;
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
      };
    }, [fetchDesignDirections, campaignId]);

    // Handle selection - sends message to chat with DD ID for the backend to use
    const handleSelect = useCallback(
      (dd: DesignDirection) => {
        // Prevent multiple selections
        if (hasSelected) return;
        setHasSelected(true);
        hasSelectedRef.current = true;

        // Immediately show only the selected DD (hide the other 2)
        // This prevents layout shift when the backend later deletes non-selected DDs
        const selectedOnly = [dd];
        setDesignDirections(selectedOnly);
        selectedCampaignCache.set(campaignId, selectedOnly);

        if (onSelect) {
          onSelect(dd.id);
        }
        // Send user-friendly message with hidden ID for backend
        // The [designDirectionId:xxx] part is stripped from display in Message.tsx
        // Polling continues until backend deletes the other DDs (campaign returns 1 DD)
        sendMessage(`I'll go with Design ${dd.variant} [designDirectionId:${dd.id}]`);
      },
      [onSelect, sendMessage, hasSelected, campaignId]
    );

    // Handle regeneration - sends message to chat with DD ID
    const handleRegenerate = useCallback(
      (dd: DesignDirection, feedback?: string) => {
        if (onRegenerate) {
          onRegenerate(dd.id, feedback);
        }
        // Send message to chat about regeneration - include ID so backend can use it directly
        const feedbackText = feedback ? ` with feedback: ${feedback}` : '';
        sendMessage(`Regenerate design direction ${dd.variant} [designDirectionId:${dd.id}]${feedbackText}`);
      },
      [onRegenerate, sendMessage]
    );

    // Loading state — show neutral Fusion loading while the first fetch is in flight.
    // This avoids the flash of the 3-step stepper starting from scratch on page refresh
    // when the DDs are actually already generated.
    if (isLoading) {
      return (
        <Box w="100%" maxW="100%" my={4}>
          <Flex justify="center" align="center" minH="200px">
            <FusionLoading isLoading />
          </Flex>
        </Box>
      );
    }

    // Error state
    if (error) {
      return (
        <Box
          w="100%"
          maxW="100%"
          my={4}
          p={6}
          bg="red.50"
          borderRadius="lg"
          border="1px solid"
          borderColor="red.200"
        >
          <VStack spacing={3}>
            <Text color="red.600" textAlign="center">
              {error}
            </Text>
            <Button
              colorScheme="orange"
              variant="outline"
              size="sm"
              onClick={fetchDesignDirections}
            >
              Try Again
            </Button>
          </VStack>
        </Box>
      );
    }

    // Empty state - DDs still generating
    if (designDirections.length === 0) {
      return (
        <Box w="100%" maxW="100%" my={4}>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <LoadingCard variant="A" />
            <LoadingCard variant="B" />
            <LoadingCard variant="C" />
          </SimpleGrid>
        </Box>
      );
    }

    // Check if any DDs are still generating
    const hasGenerating = designDirections.some(dd => dd.status !== 'GENERATED');

    return (
      <Box
        w="100%"
        maxW="100%"
        my={4}
        position="relative"
      >
        {hasGenerating && (
          <Flex align="center" justify="center" gap={2} mb={3}>
            <Spinner size="sm" color="orange.500" />
            <Text fontSize="sm" color="gray.500">Some designs are still generating...</Text>
          </Flex>
        )}

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          {designDirections.map((dd) => (
            <DDPreviewCard
              key={dd.id}
              designDirection={dd}
              campaignId={campaignId}
              isSelected={hasSelected || selectedDesignDirectionId === dd.id}
              onSelect={() => handleSelect(dd)}
              onRegenerate={(feedback) => handleRegenerate(dd, feedback)}
            />
          ))}
        </SimpleGrid>
      </Box>
    );
  }
);

DDOptionsGrid.displayName = 'DDOptionsGrid';

export default DDOptionsGrid;
